/**
 * Device Detection Utilities
 * Provides comprehensive device and browser detection for deep link handling
 */

/**
 * Detect device type and platform
 */
export const detectDevice = () => {
  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  
  const detection = {
    userAgent,
    platform,
    isIOS: isIOSDevice(userAgent),
    isAndroid: isAndroidDevice(userAgent),
    isMobile: false,
    isDesktop: false,
    isTablet: false,
    browser: detectBrowser(userAgent),
    osVersion: null
  };

  // Set device categories
  detection.isMobile = detection.isIOS || detection.isAndroid;
  detection.isTablet = isTabletDevice(userAgent);
  detection.isDesktop = !detection.isMobile && !detection.isTablet;

  // Get OS version
  if (detection.isIOS) {
    detection.osVersion = getIOSVersion(userAgent);
  } else if (detection.isAndroid) {
    detection.osVersion = getAndroidVersion(userAgent);
  }

  return detection;
};

/**
 * Check if device is iOS
 */
export const isIOSDevice = (userAgent = navigator.userAgent) => /iPhone|iPad|iPod/i.test(userAgent);

/**
 * Check if device is Android
 */
export const isAndroidDevice = (userAgent = navigator.userAgent) => /android/i.test(userAgent);

/**
 * Check if device is a tablet
 */
export const isTabletDevice = (userAgent = navigator.userAgent) => /iPad|Android(?!.*Mobile)|Tablet/i.test(userAgent);

/**
 * Check if device is mobile (phone or tablet)
 */
export const isMobileDevice = () => {
  const detection = detectDevice();
  return detection.isMobile || detection.isTablet;
};

/**
 * Check if device is desktop
 */
export const isDesktopDevice = () => !isMobileDevice();

/**
 * Detect browser type
 */
export const detectBrowser = (userAgent = navigator.userAgent) => {
  const browsers = [
    { name: 'Safari', regex: /Safari/i, exclude: /Chrome|Chromium|Edge/i },
    { name: 'Chrome', regex: /Chrome/i, exclude: /Edge/i },
    { name: 'Firefox', regex: /Firefox/i },
    { name: 'Edge', regex: /Edge/i },
    { name: 'Opera', regex: /Opera|OPR/i },
    { name: 'Samsung', regex: /SamsungBrowser/i },
    { name: 'Facebook', regex: /FBAN|FBAV/i },
    { name: 'Instagram', regex: /Instagram/i },
    { name: 'Twitter', regex: /Twitter/i },
    { name: 'LinkedIn', regex: /LinkedInApp/i }
  ];

  const matchedBrowser = browsers.find(browser => browser.regex.test(userAgent) && (!browser.exclude || !browser.exclude.test(userAgent)));
  
  if (matchedBrowser) {
    return matchedBrowser.name;
  }

  return 'Unknown';
};

/**
 * Get iOS version from user agent
 */
export const getIOSVersion = (userAgent = navigator.userAgent) => {
  const match = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
  return match ? `${match[1]}.${match[2]}${match[3] ? `.${match[3]}` : ''}` : null;
};

/**
 * Get Android version from user agent
 */
export const getAndroidVersion = (userAgent = navigator.userAgent) => {
  const match = userAgent.match(/Android (\d+(?:\.\d+)*)/);
  return match ? match[1] : null;
};

/**
 * Check if device supports clipboard API
 */
export const supportsClipboardAPI = () => !!(navigator.clipboard && window.isSecureContext);

/**
 * Check if device supports modern web features
 */
export const getWebCapabilities = () => ({
  clipboard: supportsClipboardAPI(),
  serviceWorker: 'serviceWorker' in navigator,
  webShare: 'share' in navigator,
  notifications: 'Notification' in window,
  geolocation: 'geolocation' in navigator,
  camera: 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
  secureContext: window.isSecureContext
});

/**
 * Check if running in app webview
 */
export const isInAppBrowser = (userAgent = navigator.userAgent) => {
  const inAppPatterns = [
    /FBAN|FBAV/i, // Facebook
    /Instagram/i, // Instagram
    /Twitter/i, // Twitter
    /LinkedInApp/i, // LinkedIn
    /Line/i, // Line
    /WhatsApp/i, // WhatsApp
    /wv\)/i, // Generic WebView indicator
  ];

  return inAppPatterns.some(pattern => pattern.test(userAgent));
};

/**
 * Get device information for debugging
 */
export const getDeviceInfo = () => {
  const detection = detectDevice();
  const capabilities = getWebCapabilities();
  
  return {
    ...detection,
    capabilities,
    inAppBrowser: isInAppBrowser(),
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      pixelRatio: window.devicePixelRatio || 1
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    language: navigator.language || navigator.userLanguage,
    languages: navigator.languages || [navigator.language],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine
  };
};

/**
 * Log device information for debugging
 */
export const logDeviceInfo = () => {
  const info = getDeviceInfo();
  console.log('Device Information:', info);
  return info;
};

/**
 * Check if device supports deferred deep linking
 */
export const supportsDeferredDeepLinking = () => 
  // All devices support deferred deep linking, just with different behaviors
  true;

/**
 * Check if device can handle immediate deep links
 */
export const canHandleImmediateDeepLinks = () => {
  const detection = detectDevice();
  const inApp = isInAppBrowser();
  
  // Immediate deep links work best on native mobile browsers
  return (detection.isMobile || detection.isTablet) && !inApp;
};

/**
 * Get deep link handling strategy for current device
 */
export const getDeepLinkStrategy = () => {
  const detection = detectDevice();
  const inApp = isInAppBrowser();
  
  if (detection.isIOS && !inApp) {
    return 'ios-clipboard';
  }
  if (detection.isAndroid && !inApp) {
    return 'android-referrer';
  }
  if (detection.isDesktop) {
    return 'desktop-deferred';
  }
  // In-app browsers or tablets
  return 'fallback-deferred';
};

/**
 * Check if current context supports localStorage for deferred deep linking
 */
export const supportsLocalStorage = () => {
  try {
    const test = 'localStorage-test';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
};
